import { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ─────────────────────────────────────────────────────────────────────────────
// FilePreview — спільний перегляд файлів з сервера (замінює прямі
// <a target="_blank"> посилання по всьому порталу). Показує картинки/PDF
// прямо в модалці (бекенд тепер віддає їх inline — див. backend/utils/
// fileStream.js) і завжди має явну кнопку "Download file", яка працює для
// будь-якого типу файлу (через ?download=1, навіть якщо прев'ю недоступне).
// ─────────────────────────────────────────────────────────────────────────────

const IMAGE_EXT = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];

function guessKind(name = '', mime = '') {
  if ((mime || '').startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (IMAGE_EXT.includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  return 'other';
}

/** Хук-хелпер для будь-якого компонента, що рендерить список файлів:
 *  const { previewFile, openPreview, closePreview } = useFilePreview();
 *  <span onClick={() => openPreview({ url, downloadUrl, name, mime })}>...</span>
 *  <FilePreviewModal file={previewFile} onClose={closePreview} /> */
export function useFilePreview() {
  const [file, setFile] = useState(null);
  const openPreview  = useCallback(f => setFile(f), []);
  const closePreview = useCallback(() => setFile(null), []);
  return { previewFile: file, openPreview, closePreview };
}

export function FilePreviewModal({ file, onClose }) {
  if (!file) return null;
  const { url, downloadUrl, name, mime } = file;
  const kind = guessKind(name, mime);

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', flexDirection: 'column' }}
      onClick={onClose}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', background: 'var(--bg-menu)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}
        onClick={e => e.stopPropagation()}>
        <span style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        <a href={downloadUrl || url} download={name}
          style={{ background: 'var(--info-text)', color: 'var(--on-accent)', textDecoration: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          ⬇ Download file
        </a>
        <button onClick={onClose}
          style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-secondary)', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }}>
          ✕ Close
        </button>
      </div>

      <div
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: kind === 'other' ? '40px' : 0 }}
        onClick={e => e.stopPropagation()}>
        {kind === 'image' && (
          <img src={url} alt={name} style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain' }} />
        )}
        {kind === 'pdf' && (
          <iframe src={url} title={name} style={{ width: '100%', height: '100%', border: 'none', background: 'var(--text-primary)' }} />
        )}
        {kind === 'other' && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📎</div>
            <p style={{ fontSize: '14px', marginBottom: '18px', lineHeight: 1.5 }}>
              Preview isn't available for this file type.<br />Download it to view.
            </p>
            <a href={downloadUrl || url} download={name}
              style={{ background: 'var(--info-text)', color: 'var(--on-accent)', textDecoration: 'none', padding: '10px 22px', borderRadius: '8px', fontSize: '14px', fontWeight: 700 }}>
              ⬇ Download file
            </a>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
